<template>
  <div class="immunization-rates">
    <filters-section v-if="global.configLoaded" :filterDisplayKeys="global.filterKeys" :dateRangeStart="global.defaultDateStart" :defaultValues="global.defaultFilters" :modifiedDatepickerShortcuts="global.datepickerShortcuts" :moreFilters="global.additionalFilters" :typeDrivenDateDefaults="true" v-on:change="formatFilters">
      <div slot="immunizationDate-append">
        <el-button data-testid="use-expiry-date" :plain="true" class="filter-append" size="mini" @click="useExpiryDateFilter">Use Expiry Date</el-button>
      </div>
      <div slot="expiryDate-append">
        <el-button data-testid="use-administration-date" :plain="true" class="filter-append" size="mini" @click="useAdministrationDateFilter">Use Administration Date</el-button>
      </div>
    </filters-section>
    <page-section title="Graph" v-loading="graphSection.loading">
      <template slot="title">
        <span class="label do-not-print">Group by</span>
        <el-radio-group class="do-not-print" v-model="graphSection.groupBy" size="small" @change="refreshAllData">
          <el-radio-button label="Immunization Type"></el-radio-button>
          <el-radio-button label="Vaccine Type"></el-radio-button>
          <el-radio-button label="Individual Type"></el-radio-button>
          <el-radio-button label="Facility"></el-radio-button>
          <el-radio-button label="Employee Type"></el-radio-button>
        </el-radio-group>
        <!-- unneeded as currently there is only one timescale -->
        <!-- <span class="label">and</span>
        <el-radio-group v-model="graphSection.timeScale" size="small">
          <el-radio-button label="Year"></el-radio-button>
        </el-radio-group> -->
      </template>
      <div style="margin: 10px">
        <stacked-column-chart printable :title="graphSection.title" :series="graphSection.series" :xAxisCategories="graphSection.xAxis" :yAxisTitle="graphSection.yAxis" :yAxisPlotLines="graphSection.plotLines" :usePerColumnLabels="true" :configuration="graphSection.customizations"></stacked-column-chart>
      </div>
    </page-section>

    <page-section class="do-not-print" title="Summary" collapsible collapsed>
      <el-table :data="summarySection.data">
        <el-table-column prop="label" :label="graphSection.groupBy">
          <template slot-scope="scope">
            {{ scope.row[graphSection.groupOptions[graphSection.groupBy]] }}
          </template>
        </el-table-column>
        <el-table-column label="Rate">
          <template slot-scope="scope"> {{ scope.row.total }}% ({{ scope.row.count }}/{{ scope.row.denominator }}) </template>
        </el-table-column>
        <el-table-column label="Declined">
          <template slot-scope="scope"> {{ scope.row.declinedRate }}% ({{ scope.row.declinedCount }}/{{ scope.row.denominator }}) </template>
        </el-table-column>
      </el-table>
      <p class="presence-note" v-if="summarySection.presence && summarySection.presence.excludedUnknownCount > 0">{{ summarySection.presence.excludedUnknownCount }} discharged individuals have no derivable discharge date</p>
    </page-section>

    <page-section class="only-print" title="Summary" :set="tableGridColumns = [{ key:graphSection.groupOptions[graphSection.groupBy], label: graphSection.groupBy, width: 'max-content'}, { key :'rate',  label: 'Rate', width: '1fr'}, { key: 'declined', label: 'Declined', width: '1fr'}]">
      <TableGrid :columns="tableGridColumns" :rows="summarySection.data">
        <template slot="columns" slot-scope="scope">
          <div v-for="col in tableGridColumns">
            <template v-if="col.key === 'rate'">
              {{ scope.row.total }}% ({{ scope.row.count }}/{{ scope.row.denominator }})
            </template>
            <template v-else-if="col.key === 'declined'">
              {{ scope.row.declinedRate }}% ({{ scope.row.declinedCount }}/{{ scope.row.denominator }})
            </template>
            <template v-else>
              {{ scope.row[col.key] }}
            </template>
          </div>
        </template>
      </TableGrid>
      <p class="presence-note" v-if="summarySection.presence && summarySection.presence.excludedUnknownCount > 0">{{ summarySection.presence.excludedUnknownCount }} discharged individuals have no derivable discharge date</p>
    </page-section>


    <page-section class="do-not-print" title="Analysis Details" v-if="!immunizationsSection.loading">
      <template slot="title">
        <el-button data-testid="sync-immunizations" size="small" v-if="global.canSync" @click="openSyncImmunizations" type="warning">Sync Immunizations</el-button>
        <el-button data-testid="export-data" size="small" @click="exportImmunizationsToExcel()">Export Data</el-button>
      </template>

      <div class="analysisPanels">
        <div>
          <Panel>
            <h2 slot="title">Individuals with records {{ detailSections.immunizedCount }}</h2>
            <el-table :data="immunizationsSection.data">
              <el-table-column label="Name" width="150" fixed>
                <template scope="scope">
                  <div>
                    {{ scope.row.name }}
                    <el-tag v-if="scope.row.dischargeDateUnknown" size="mini" type="warning" :title="dateUnknownLabel">{{ dateUnknownBadge }}</el-tag>
                  </div>
                  ({{ scope.row.client.staffId || scope.row.client.patientId }})
                </template>
              </el-table-column>
              <el-table-column v-if="global.facilities.length > 1" prop="facility" label="Facility" min-width="200"> </el-table-column>
              <el-table-column prop="description" label="Immunization Type" min-width="150"> </el-table-column>
              <el-table-column prop="vaccineType" label="Vaccine Type" min-width="150"> </el-table-column>
              <el-table-column prop="location" label="Location" min-width="100"> </el-table-column>
              <el-table-column prop="lotNumber" label="Lot Number" min-width="100"> </el-table-column>
              <el-table-column prop="dateFormatted" label="Administration Date" min-width="100"> </el-table-column>
              <el-table-column prop="expiryFormatted" label="Expiry Date" min-width="100"> </el-table-column>
            </el-table>
            <el-pagination
              @size-change="
                size => {
                  immunizationsSection.pagination.size = size;
                }
              "
              @current-change="
                page => {
                  immunizationsSection.pagination.page = page;
                }
              "
              :page-sizes="[5, 10, 20, 100]"
              :page-size="immunizationsSection.pagination.size"
              layout="sizes, prev, pager, next"
              :total="immunizationsSection.pagination.total"
            >
            </el-pagination>
          </Panel>
        </div>
        <div>
          <Panel :isLoading="detailSections.declinedLoading">
            <h2 slot="title">Individuals with declinations {{ detailSections.declinedCount }}</h2>
            <el-table :data="detailSections.declinedClients">
              <el-table-column label="Name" width="150" fixed>
                <template slot-scope="scope">
                  {{ scope.row.name }}
                  <el-tag v-if="scope.row.dischargeDateUnknown" size="mini" type="warning" :title="dateUnknownLabel">{{ dateUnknownBadge }}</el-tag>
                </template>
              </el-table-column>
              <el-table-column v-if="global.facilities.length > 1" prop="facility" label="Facility" min-width="200"> </el-table-column>
              <el-table-column prop="description" label="Immunization Type" min-width="150"> </el-table-column>
              <el-table-column prop="vaccineType" label="Vaccine Type" min-width="150"> </el-table-column>
              <el-table-column prop="location" label="Location" min-width="100"> </el-table-column>
              <el-table-column prop="lotNumber" label="Lot Number" min-width="100"> </el-table-column>
              <el-table-column prop="declinedReason" label="Reason" min-width="100"> </el-table-column>
              <el-table-column prop="dateFormatted" label="Administration Date" min-width="100"> </el-table-column>
              <el-table-column prop="expiryFormatted" label="Expiry Date" min-width="100"> </el-table-column>
            </el-table>
            <el-pagination
              @size-change="
                size => {
                  detailSections.declinedPagination.size = size;
                }
              "
              @current-change="
                page => {
                  detailSections.declinedPagination.page = page;
                }
              "
              :page-sizes="[5, 10, 20, 100]"
              :page-size="detailSections.declinedPagination.size"
              layout="sizes, prev, pager, next"
              :total="detailSections.declinedPagination.total"
            >
            </el-pagination>
          </Panel>
        </div>
        <div>
          <Panel :isLoading="summarySection.clientsWithoutLoading">
            <h2 slot="title">Individuals without records {{ summarySection.pagination.total }}</h2>
            <el-table :data="summarySection.clientsWithoutImmunizations">
              <el-table-column label="Name">
                <template slot-scope="scope">
                  {{ scope.row.name }}
                  <el-tag v-if="scope.row.dischargeDateUnknown" size="mini" type="warning" :title="dateUnknownLabel">{{ dateUnknownBadge }}</el-tag>
                </template>
              </el-table-column>
              <el-table-column prop="id" label="ID"></el-table-column>
              <el-table-column prop="staff" label="Staff?"></el-table-column>
            </el-table>
            <el-pagination
              @size-change="
                size => {
                  summarySection.pagination.size = size;
                }
              "
              @current-change="
                page => {
                  summarySection.pagination.page = page;
                }
              "
              :page-sizes="[5, 10, 20, 100]"
              :page-size="summarySection.pagination.size"
              layout="sizes, prev, pager, next"
              :total="summarySection.pagination.total"
            >
            </el-pagination>
          </Panel>
        </div>
      </div>
    </page-section>
    <el-dialog v-if="immunizationsSection.immunizationSyncVisible" v-model="immunizationsSection.immunizationSyncVisible" title="Sync Immunizations" :show-close="false" size="tiny">
      <sync-immunizations
        v-on:immunization-sync-close="closeImmunizationSync"
        v-on:immunization-sync-complete="
          closeImmunizationSync();
          refreshAllData();
        "
      ></sync-immunizations>
    </el-dialog>
  </div>
</template>

<script>
import moment from "moment";
import { downloadXlsx } from "@/spreadsheet";
import util from "@/util";
import auth from "@/auth";
import FiltersSection from "../../Shared/FiltersSection";
import PageSection from "../../Shared/PageSection";
import StackedColumnChart from "../../Shared/StackedColumnChart";
import SyncImmunizationsForFacility from "../../Immunizations/SyncImmunizationsForFacility";
import Panel from "@/components/Shared/Panel.vue";
import TableGrid from "@/components/Shared/TableGrid.vue";

const SECTION_QUERY_VALUES = { immunized: "immunized", declined: "declined", noRecord: "noRecord" };
const SECTION_LABELS = {
  immunized: "Individuals with records",
  declined: "Individuals with declinations",
  noRecord: "Individuals without records"
};
const COUNT_PROBE_PAGE = { page: 1, size: 1 };
const EMPTY_CLIENTS_PAGE = { totalElements: 0, content: [] };
const EMPTY_DECLINED_PAGE = { totalElements: 0, totalIndividuals: 0, content: [] };
const CLIENT_TYPE_STAFF = "Staff";
const DEFAULT_PATIENT_ID_KEY = "patientId";
const ROLE_ADMIN_STAFF = "ROLE_ADMIN_STAFF";
const CLIENT_TYPE_FILTER_KEY = "clientType";
const CLIENT_STATUS_FILTER_KEY = "clientStatus";

// Same rule as OM/OutbreakDetail.vue and OM/ManageInfectionCases.vue: without
// staff access the Individual Type filter is not offered at all, rather than
// offered with an option the API rejects with 403.
function isAdminStaff() {
  // userInfo() returns {} when localStorage.user is absent (auth.js). Unlike the
  // precedents below this runs from data(), where a throw blanks the whole page
  // instead of hiding one filter.
  return (auth.userInfo().roles || []).indexOf(ROLE_ADMIN_STAFF) >= 0;
}
const INCLUDE_PRESENT_DURING_PERIOD_FILTER_KEY = "includePresentDuringPeriod";
const IMMUNIZATION_DATE_FILTER_KEY = "immunizationDate";
const REPORTING_PERIOD_FILTER_KEY = "reportingPeriod";
const REPORTING_PERIOD_DEFAULT_DAYS = 30;
const PRESENCE_EXPORT_COLUMN_HEADER = "Presence";
// Spec §5.4's sanctioned label — must match the API's DATE_UNKNOWN_LABEL exactly.
// Shown in full as the badge tooltip; the export's Presence column carries it verbatim from the API.
const DATE_UNKNOWN_LABEL = "Discharged — date unknown";
// Short badge text: the full label does not fit the fixed 150px Name column.
const DATE_UNKNOWN_BADGE = "Unknown discharge";

export default {
  name: "medication-usage-page",
  components: {
    "filters-section": FiltersSection,
    "page-section": PageSection,
    "stacked-column-chart": StackedColumnChart,
    "sync-immunizations": SyncImmunizationsForFacility,
    Panel,
    TableGrid
  },
  watch: {
    "immunizationsSection.pagination.size"() {
      this.getImmunizations();
    },
    "immunizationsSection.pagination.page"() {
      this.getImmunizations();
    },
    "summarySection.pagination.size"() {
      this.getMissingImmunizations();
    },
    "summarySection.pagination.page"() {
      this.getMissingImmunizations();
    },
    "detailSections.declinedPagination.size"() {
      this.getDeclinedSectionRecords();
    },
    "detailSections.declinedPagination.page"() {
      this.getDeclinedSectionRecords();
    }
  },
  data() {
    return {
      dateUnknownLabel: DATE_UNKNOWN_LABEL,
      dateUnknownBadge: DATE_UNKNOWN_BADGE,
      global: {
        canSync: false,
        exportLimit: 10000,
        facilities: [],
        filters: {},
        configLoaded: false,
        periodPresenceEnabled: false,
        filterKeys: [
          "immunizationTypes",
          "vaccineTypes",
          "immunizationDate",
          ...(isAdminStaff() ? [CLIENT_TYPE_FILTER_KEY] : []),
          CLIENT_STATUS_FILTER_KEY,
          "oneRecordOnly"
        ],
        additionalFilters: ["firstName", "lastName", "employeeTypes"],
        // The Reporting Period default is added in fetchImmunizationConfig,
        // not here: it must appear only alongside the control that can edit
        // it, which exists only when the period-presence gate is on.
        defaultFilters: {
          clientStatus: "C",
          declined: "all"
        },
        defaultDateStart: moment()
          .startOf("year")
          .toDate(),
        datepickerShortcuts: [
          {
            text: "All dates",
            onClick(picker) {
              picker.$emit("pick", "");
            }
          },
          ...util.datepickerShortcuts()
        ]
      },
      immunizationsSection: {
        loading: true,
        exporting: false,
        immunizationSyncVisible: false,
        exportProgress: 0,
        data: [],
        pagination: {
          page: 1,
          size: 10,
          total: 0
        }
      },
      graphSection: {
        loading: true,
        groupBy: "Immunization Type",
        timeScale: "Year",
        groupOptions: {
          "Immunization Type": "immunizationType",
          "Vaccine Type": "vaccineType",
          "Individual Type": "clientType",
          Facility: "facility",
          "Employee Type": "employeeType"
        },
        series: [],
        title: "Immunization Rates",
        yAxis: "Immunization Rate (%)",
        xAxis: [],
        plotLines: [],
        customizations: {
          legend: {
            enabled: true
          },
          plotOptions: {
            series: {
              dataLabels: {
                format: "{y}%"
              }
            },
            column: {
              stacking: undefined
            }
          }
        }
      },
      summarySection: {
        loading: true,
        data: [],
        columns: [],
        clientsWithoutImmunizations: [],
        exportModels: {},
        clientsWithoutLoading: true,
        presence: null,
        pagination: {
          page: 1,
          size: 10,
          total: 0
        }
      },
      detailSections: {
        immunizedCount: 0,
        declinedCount: 0,
        declinedClients: [],
        declinedLoading: false,
        declinedPagination: { page: 1, size: 10, total: 0 }
      }
    };
  },
  methods: {
    formatFilters(rawFilters) {
      const filterValues = Object.fromEntries(
        Object.entries(rawFilters)
          .filter(([k, v]) => v !== 0)
          .map(([k, v]) => {
            switch (k) {
              case "facilities":
                k = "facIds";
                break;
              case "units":
                k = "unitIds";
                break;
            }
            return [k, v];
          })
      );
      this.global.filters = { ...filterValues };
      delete this.global.filters.expiryDateRangeStart;
      delete this.global.filters.expiryDateRangeEnd;
      delete this.global.filters.dateRangeEnd;
      delete this.global.filters.dateRangeStart;
      delete this.global.filters.immunizationDate;
      delete this.global.filters.expiryDate;
      // periodStart/periodEnd are deliberately NOT deleted here. The date-source
      // toggles re-enter through `{...this.global.filters}`, which carries the
      // derived pair but never the raw `reportingPeriod` key (deleted just below),
      // so deleting them would silently drop the Reporting Period on every toggle.
      delete this.global.filters[REPORTING_PERIOD_FILTER_KEY];
      if (filterValues.immunizationDate && filterValues.immunizationDate[0]) {
        this.global.filters.dateRangeStart = moment(filterValues.immunizationDate[0])
          .startOf("day")
          .format();
        this.global.filters.dateRangeEnd = moment(filterValues.immunizationDate[1])
          .endOf("day")
          .format();
      }
      if (filterValues.expiryDate && filterValues.expiryDate[0]) {
        this.global.filters.expiryDateRangeStart = moment(filterValues.expiryDate[0])
          .startOf("day")
          .format();
        this.global.filters.expiryDateRangeEnd = moment(filterValues.expiryDate[1])
          .endOf("day")
          .format();
      }
      const reportingPeriod = filterValues[REPORTING_PERIOD_FILTER_KEY];
      if (reportingPeriod && reportingPeriod[0]) {
        this.global.filters.periodStart = moment(reportingPeriod[0])
          .startOf("day")
          .format();
        this.global.filters.periodEnd = moment(reportingPeriod[1])
          .endOf("day")
          .format();
      }

      if (this.global.filters.declined === "all") {
        delete this.global.filters.declined;
      }
      if (this.global.filters[INCLUDE_PRESENT_DURING_PERIOD_FILTER_KEY] === false) {
        delete this.global.filters[INCLUDE_PRESENT_DURING_PERIOD_FILTER_KEY];
      }
      this.refreshAllData();
    },
    async fetchImmunizationConfig() {
      try {
        const response = await this.$http.get(`${window.CONFIG.immunization_api}/config`).then(res => res.body);
        this.global.periodPresenceEnabled = !!response.periodPresenceEnabled;
      } catch (err) {
        this.global.periodPresenceEnabled = false;
      }
      if (this.global.periodPresenceEnabled) {
        const clientStatusIndex = this.global.filterKeys.indexOf(CLIENT_STATUS_FILTER_KEY);
        this.global.filterKeys.splice(clientStatusIndex + 1, 0, INCLUDE_PRESENT_DURING_PERIOD_FILTER_KEY);
        // HCI-4901: the Reporting Period sits with the Administration Date it
        // was split out of. HCI-4903: the Status default stays Active — under
        // presence it now means "active during the period", so it no longer
        // has to be cleared.
        const administrationDateIndex = this.global.filterKeys.indexOf(IMMUNIZATION_DATE_FILTER_KEY);
        this.global.filterKeys.splice(administrationDateIndex, 0, REPORTING_PERIOD_FILTER_KEY);
        this.global.defaultFilters[REPORTING_PERIOD_FILTER_KEY] = [
          moment()
            .subtract(REPORTING_PERIOD_DEFAULT_DAYS, "days")
            .startOf("day")
            .toDate(),
          moment()
            .endOf("day")
            .toDate()
        ];
      }
      this.global.configLoaded = true;
    },
    async getImmunizations() {
      const json = await this.$http
        .get(`${window.CONFIG.immunization_api}`, {
          params: {
            ...this.global.filters,
            groupBy: this.graphSection.groupOptions[this.graphSection.groupBy],
            timeScale: this.graphSection.timeScale.toLowerCase(),
            declined: false,
            page: this.immunizationsSection.pagination.page,
            size: this.immunizationsSection.pagination.size
          }
        })
        .then(response => response.body)
        .catch(err => {
          if (err.status === 0) {
            this.immunizationsSection.loadingErrorDialog = true;
          }
          this.immunizationsSection.loading = false;
          return {
            page: 0,
            content: [],
            totalElements: 0
          };
        });
      this.immunizationsSection.pagination.total = json.totalElements;
      this.immunizationsSection.data = json.content.map(this.generateImmunizationsTableData);
      this.immunizationsSection.loading = false;
    },
    generateImmunizationsTableData(record) {
      let model = { ...record };
      model.dateFormatted = moment(record.date).format(this.$configStore.dateFormat());
      model.expiryFormatted = record.expiry ? moment(record.expiry).format(this.$configStore.dateFormat()) : "";
      model.facility = this.global.facilities.filter(s => s.id === Number(record.client.facId))[0].displayText;
      model.declinedFormatted = record.declined ? "Yes" : "No";
      const { firstName, lastName, middleName } = model.client;
      model.name = [firstName, middleName, lastName].filter(Boolean).join(" ");
      model.dischargeDateUnknown = !!record.client.dischargeDateUnknown;
      return model;
    },
    getImmunizationExport() {
      return this.$http
        .get(`${window.CONFIG.immunization_api}/export`, {
          params: {
            ...this.global.filters,
            groupBy: this.graphSection.groupOptions[this.graphSection.groupBy],
            timeScale: this.graphSection.timeScale.toLowerCase(),
            declined: false,
            tz: Intl.DateTimeFormat().resolvedOptions().timeZone
          }
        })
        .then(response => response.body);
    },
    getDeclinedExport() {
      return this.$http
        .get(`${window.CONFIG.immunization_api}/declined-export`, {
          params: {
            ...this.global.filters,
            groupBy: this.graphSection.groupOptions[this.graphSection.groupBy],
            timeScale: this.graphSection.timeScale.toLowerCase(),
            tz: Intl.DateTimeFormat().resolvedOptions().timeZone
          }
        })
        .then(response => response.body);
    },
    async getSummary() {
      this.graphSection.loading = true;
      this.summarySection.loading = true;
      const summaryResponse = await this.$http
        .get(`${window.CONFIG.immunization_api}/summary`, {
          params: {
            ...this.global.filters,
            groupBy: this.graphSection.groupOptions[this.graphSection.groupBy],
            timeScale: this.graphSection.timeScale.toLowerCase(),
            tz: Intl.DateTimeFormat().resolvedOptions().timeZone
          }
        })
        .then(res => res.body)
        .catch(() => {
          this.summarySection.loading = false;
          this.graphSection.loading = false;
          return { groups: [] };
        });

      // Deploy-order safety (HCI-4864): an old API returns the summary as a
      // bare groups array, not {groups, presence}. Read the array shape
      // directly, and only look for `presence` on the new object shape.
      const summary = Array.isArray(summaryResponse) ? summaryResponse : summaryResponse.groups || [];
      this.summarySection.presence = (!Array.isArray(summaryResponse) && summaryResponse.presence) || null;
      const [groupedTotals, groups] = this.createSummary(summary);
      this.summarySection.exportModels = { groupedTotals, groups };
      this.generateSummaryTableData(summary);
      this.generateGraphData(groupedTotals, groups);
      this.graphSection.loading = false;
      this.summarySection.loading = false;
    },
    // Single fetcher for the three `/clients` detail sections. Never rejects: a
    // failed request resolves to an empty page so callers' loading flags always clear.
    async fetchClientsSection(section, paging) {
      try {
        const response = await this.$http.get(`${window.CONFIG.immunization_api}/clients`, {
          params: {
            ...this.global.filters,
            section,
            groupBy: this.graphSection.groupOptions[this.graphSection.groupBy],
            timeScale: this.graphSection.timeScale.toLowerCase(),
            tz: Intl.DateTimeFormat().resolvedOptions().timeZone,
            ...paging
          }
        });
        return response.body;
      } catch (err) {
        return { ...EMPTY_CLIENTS_PAGE };
      }
    },
    // One row shape for the "with declinations" and "without records" panels; the
    // name format matches the records panel (generateImmunizationsTableData).
    toClientRow({ firstName, middleName, lastName, clientType, staffId, dischargeDateUnknown, ...client }, patientIdLookupKey) {
      const isStaff = clientType === CLIENT_TYPE_STAFF;
      return {
        name: [firstName, middleName, lastName].filter(Boolean).join(" "),
        id: isStaff ? staffId : client[patientIdLookupKey],
        staff: isStaff ? "Yes" : "",
        dischargeDateUnknown: !!dischargeDateUnknown
      };
    },
    patientIdLookupKey() {
      return this.$configStore.patientIdType() || DEFAULT_PATIENT_ID_KEY;
    },
    async getMissingImmunizations() {
      this.summarySection.clientsWithoutLoading = true;
      try {
        const { page, size } = this.summarySection.pagination;
        const response = await this.fetchClientsSection(SECTION_QUERY_VALUES.noRecord, { page, size });
        const patientIdLookupKey = this.patientIdLookupKey();
        this.summarySection.pagination.total = response.totalElements;
        this.summarySection.clientsWithoutImmunizations = response.content.map(client => this.toClientRow(client, patientIdLookupKey));
      } finally {
        this.summarySection.clientsWithoutLoading = false;
      }
    },
    async getImmunizedSectionCount() {
      const response = await this.fetchClientsSection(SECTION_QUERY_VALUES.immunized, COUNT_PROBE_PAGE);
      this.detailSections.immunizedCount = response.totalElements;
    },
    async getDeclinedSectionRecords() {
      this.detailSections.declinedLoading = true;
      try {
        const { page, size } = this.detailSections.declinedPagination;
        let response;
        try {
          const result = await this.$http.get(`${window.CONFIG.immunization_api}/declined`, {
            params: {
              ...this.global.filters,
              groupBy: this.graphSection.groupOptions[this.graphSection.groupBy],
              timeScale: this.graphSection.timeScale.toLowerCase(),
              tz: Intl.DateTimeFormat().resolvedOptions().timeZone,
              page,
              size
            }
          });
          response = result.body;
        } catch (err) {
          response = { ...EMPTY_DECLINED_PAGE };
        }
        // rows are records; the panel title counts individuals
        const { content = [], totalElements = 0, totalIndividuals = 0 } = response || {};
        this.detailSections.declinedPagination.total = totalElements;
        this.detailSections.declinedCount = totalIndividuals;
        this.detailSections.declinedClients = content.map(record => this.generateImmunizationsTableData(record));
      } finally {
        this.detailSections.declinedLoading = false;
      }
    },
    generateGraphData(groupedTotals, groups) {
      this.graphSection.xAxis = groups;
      this.graphSection.series = [
        {
          name: "Immunized",
          data: groups.map(group => groupedTotals.get(group).rate),
          dataLabels: { enabled: true }
        },
        {
          name: "Declined",
          data: groups.map(group => groupedTotals.get(group).declinedRate),
          dataLabels: { enabled: true }
        }
      ];
    },
    roundTwoDecimalPlaces(value) {
      return Math.round(value * 100) / 100;
    },
    generateSummaryTableData(json) {
      this.summarySection.data = json;
    },
    createSummary(summary) {
      const groupKey = this.graphSection.groupOptions[this.graphSection.groupBy];

      const groupedTotals = new Map();
      summary.forEach(row => {
        const group = row[groupKey];
        groupedTotals.set(group, {
          rate: row.total,
          declinedRate: row.declinedRate,
          declinedCount: row.declinedCount
        });
      });

      return [groupedTotals, [...groupedTotals.keys()]];
    },
    async refreshAllData() {
      this.getSummary();
      this.getImmunizations();
      this.getMissingImmunizations();
      this.getImmunizedSectionCount();
      this.getDeclinedSectionRecords();
    },
    exportClientName(client, patientIdLookupKey) {
      // "Last name, First name (client id)" — the parenthetical is omitted when no id is available.
      const clientId = client.staffId || client[patientIdLookupKey];
      const baseName = `${client.lastName}, ${client.firstName}`;
      return clientId ? `${baseName} (${clientId})` : baseName;
    },
    // The API caps each export endpoint at EXPORT_LIMIT rows. The panel counts are
    // individuals, and a person can carry several records, so truncation is judged
    // on the rows the export endpoints actually returned rather than on panel counts.
    async confirmTruncatedExport(sectionRows) {
      const truncated = sectionRows.filter(s => s.rows.length >= this.global.exportLimit);
      if (truncated.length === 0) {
        return true;
      }
      try {
        await this.$confirm(
          `The export limit is ${this.global.exportLimit} rows per section. These sections were truncated: ${truncated.map(s => s.label).join(", ")}. Export anyway?`,
          "Export limit exceeded",
          { confirmButtonText: "Export truncated file", cancelButtonText: "Cancel", type: "warning" }
        );
        return true;
      } catch (e) {
        return false; // user cancelled
      }
    },
    async exportImmunizationsToExcel() {
      this.immunizationsSection.exporting = true;
      this.immunizationsSection.exportProgress = 0;
      const patientIdLookupKey = this.patientIdLookupKey();
      // The Presence column only carries data when the period-presence filter is on (HCI-4864).
      const presenceColumnEnabled = this.global.periodPresenceEnabled && this.global.filters[INCLUDE_PRESENT_DURING_PERIOD_FILTER_KEY] === true;
      const baseColumns = ["Name", "Facility", "Immunization Type", "Vaccine Type", "Location", "Lot Number", "Declined?", "Reason", "Administration Date", "Expiry Date"];
      const columns = presenceColumnEnabled ? [...baseColumns, PRESENCE_EXPORT_COLUMN_HEADER] : baseColumns;
      const withPresenceCell = (row, presence) => (presenceColumnEnabled ? [...row, presence ?? ""] : row);
      const convertDataToColumns = json => {
        const data = this.generateImmunizationsTableData(json);
        const { client, facility, description, vaccineType, location, lotNumber, declinedFormatted, declinedReason, dateFormatted, expiryFormatted } = data;
        const clientName = this.exportClientName(client, patientIdLookupKey);
        // `Presence` is only present on declined-export rows (HCI-4864); the
        // spread in generateImmunizationsTableData carries it through untouched.
        return withPresenceCell([clientName, facility, description, vaccineType, location, lotNumber, declinedFormatted, declinedReason, dateFormatted, expiryFormatted], data.Presence);
      };
      const hasRecords = (await this.getImmunizationExport()).flat();
      const declinedRecords = await this.getDeclinedExport();
      const missingRecords = await this.$http
        .get(`${window.CONFIG.immunization_api}/blank-export`, {
          params: {
            ...this.global.filters,
            groupBy: this.graphSection.groupOptions[this.graphSection.groupBy],
            timeScale: this.graphSection.timeScale.toLowerCase(),
            tz: Intl.DateTimeFormat().resolvedOptions().timeZone
          }
        })
        .then(res => res.body);

      if (!this.immunizationsSection.exporting) {
        return;
      }

      const proceed = await this.confirmTruncatedExport([
        { label: SECTION_LABELS.immunized, rows: hasRecords },
        { label: SECTION_LABELS.declined, rows: declinedRecords },
        { label: SECTION_LABELS.noRecord, rows: missingRecords }
      ]);
      if (!proceed) {
        this.immunizationsSection.exporting = false;
        return;
      }

      const sectionHeader = label => [[label], []]; // label row + spacer row
      const data = [
        ...sectionHeader(SECTION_LABELS.immunized),
        ...hasRecords.map(convertDataToColumns),
        ...sectionHeader(SECTION_LABELS.declined),
        ...declinedRecords.map(convertDataToColumns),
        ...sectionHeader(SECTION_LABELS.noRecord),
        ...missingRecords.map(({ client, facility }) => {
          const clientName = this.exportClientName(client, patientIdLookupKey);
          return withPresenceCell([clientName, facility, "", "", "", "", "", "", "", ""], "");
        })
      ];
      await this.createExcelFile(`immunization-records-${moment().format("YYYY-MM-DD")}.xlsx`, columns, data);
      this.immunizationsSection.exporting = false;
      this.immunizationsSection.exportingErrorDialog = false;
    },
    createExcelFile: function(fileName, columns, data) {
      const detailsRow = util.generateExportDetailsRow();
      data.unshift(columns);
      data.unshift(detailsRow);
      return downloadXlsx(fileName, [{ sheet: "Sheet1", rows: data }]);
    },
    // use admin date filter instead of expiry date
    useAdministrationDateFilter() {
      let index = this.global.filterKeys.indexOf("expiryDate");

      if (index !== -1) {
        this.global.filterKeys.splice(index, 1, IMMUNIZATION_DATE_FILTER_KEY);
      }
      const filters = {
        ...this.global.filters,
        [IMMUNIZATION_DATE_FILTER_KEY]: [this.global.defaultDateStart, moment().toDate()]
      };
      delete filters.expiryDate;
      this.formatFilters(filters);
    },
    // use expiry date filter instead of admin date
    useExpiryDateFilter() {
      let index = this.global.filterKeys.indexOf(IMMUNIZATION_DATE_FILTER_KEY);

      if (index !== -1) {
        this.global.filterKeys.splice(index, 1, "expiryDate");
      }
      const filters = {
        ...this.global.filters,
        expiryDate: [this.global.defaultDateStart, moment().toDate()]
      };
      delete filters[IMMUNIZATION_DATE_FILTER_KEY];
      this.formatFilters(filters);
    },
    openSyncImmunizations() {
      this.immunizationsSection.immunizationSyncVisible = true;
    },
    closeImmunizationSync() {
      this.immunizationsSection.immunizationSyncVisible = false;
    }
  },
  async created() {
    console.clear();
    this.global.facilities = this.$configStore.data.sites;
    this.global.canSync = this.$configStore.hasAnyPccFacility() && auth.canModify();
    await this.fetchImmunizationConfig();
  }
};
</script>
<style scoped>
.presence-note {
  color: #909399;
  font-size: 0.85rem;
  margin: 0.5rem 0 0;
}

.summaryPanels {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
  padding: 1rem;
  align-items: start;
  width: 99%;
}

.analysisPanels {
  display: grid;
  grid-template-columns: 1fr;
  gap: 1rem;
  padding: 1rem;
  align-items: start;
  width: 99%;
}
.analysisPanels .el-pagination {
  display: flex;
  margin-left: auto;
}
</style>
